// pages/MediaScreenDetail/BatterySection.js
//
// Battery treatment — academic curves of how this screen's pack lives.
//
//   KPI row    — current SoC/state, plugged share, float share, deep/dead
//                time, cycles/day, median DoD, charge & discharge rates.
//   SoC curve  — the primary instrument: state-of-charge over time with
//                charging periods shaded, min–max envelope at wide windows,
//                the 0–20% stress zone washed red, curve BROKEN across
//                observation gaps (we chart what was observed, never bridge).
//   Rate trend — per-day/week median charge vs discharge rate (%/h): the
//                capacity-fade proxy. Two series, one axis, validated pair.
//   Cycle log  — the derived charge/discharge segments, tabulated.
//
// Charting follows the dataviz method: 2px lines, hairline solid grid,
// ~10% washes, crosshair + tooltip (values lead), no legend for the single
// SoC series, legend + direct labels for the two-series trend, and a table
// view so nothing is hover-gated. Palette validated:
//   discharge #4f46e5 / charge #0d9488 (ΔE 22.1 deutan, 27.1 normal, PASS).

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { ggosDeviceService } from '../../services/api';
import './BatterySection.css';

const WINDOWS = [{ h: 24, label: '24h' }, { h: 168, label: '7d' }, { h: 720, label: '30d' }];
const DIS = '#4f46e5', CHG = '#0d9488';

const fmtTick = (iso, hours) => new Date(iso).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', hour12: false,
  ...(hours <= 24 ? { hour: '2-digit', minute: '2-digit' } : { day: '2-digit', month: 'short' }),
});
const fmtFull = (iso) => new Date(iso).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', hour12: false, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
});

// ── SoC chart: single series + state shading, crosshair, keyboard focus. ──
function SocChart({ series, hours }) {
  const W = 960, H = 240, ML = 34, MR = 10, MT = 8, MB = 22;
  const iw = W - ML - MR, ih = H - MT - MB;
  const [hover, setHover] = useState(null); // bucket index
  const wrapRef = useRef(null);

  const X = (i) => ML + (i / (series.length - 1)) * iw;
  const Y = (p) => MT + (1 - p / 100) * ih;

  const { linePaths, envPath, chgBands } = useMemo(() => {
    const paths = []; let seg = [];
    const flush = () => { if (seg.length > 1) paths.push('M' + seg.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join('L')); seg = []; };
    series.forEach((b, i) => { if (b.n > 0 && b.last != null) seg.push([X(i), Y(b.last)]); else flush(); });
    flush();
    // Envelope obeys the same gap rule as the line: one closed wash PER
    // observed run, never bridging unobserved buckets.
    const envParts = [];
    if (hours > 24) {
      let tops = [], bots = [];
      const flushEnv = () => {
        if (tops.length > 1) envParts.push('M' + tops.map(p => p.map(v => v.toFixed(1)).join(',')).join('L')
          + 'L' + bots.slice().reverse().map(p => p.map(v => v.toFixed(1)).join(',')).join('L') + 'Z');
        tops = []; bots = [];
      };
      series.forEach((b, i) => {
        if (b.n > 0) { tops.push([X(i), Y(b.max)]); bots.push([X(i), Y(b.min)]); } else flushEnv();
      });
      flushEnv();
    }
    const env = envParts.join(' ');
    const bands = []; let s = null;
    series.forEach((b, i) => {
      const on = b.n > 0 && b.chgFrac >= 0.5;
      if (on && s == null) s = i;
      if (!on && s != null) { bands.push([s, i - 1]); s = null; }
    });
    if (s != null) bands.push([s, series.length - 1]);
    return { linePaths: paths, envPath: env, chgBands: bands };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, hours]);

  const nearest = (clientX) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const fx = ((clientX - rect.left) / rect.width) * W;
    let best = null, bd = Infinity;
    series.forEach((b, i) => { if (b.n > 0) { const d = Math.abs(X(i) - fx); if (d < bd) { bd = d; best = i; } } });
    return best;
  };
  const move = (di) => setHover(h => {
    if (h == null) return series.findIndex(b => b.n > 0);
    let i = h + di;
    while (i >= 0 && i < series.length && series[i].n === 0) i += di;
    return i >= 0 && i < series.length ? i : h;
  });

  const hb = hover != null ? series[hover] : null;
  const lastIdx = (() => { for (let i = series.length - 1; i >= 0; i--) if (series[i].n > 0) return i; return null; })();

  return (
    <div className="bat-chart-wrap" ref={wrapRef}>
      <svg viewBox={`0 0 ${W} ${H}`} className="bat-svg" role="img" tabIndex={0}
        aria-label="State of charge over time; arrow keys move the readout"
        onMouseMove={e => setHover(nearest(e.clientX))}
        onMouseLeave={() => setHover(null)}
        onKeyDown={e => { if (e.key === 'ArrowLeft') { move(-1); e.preventDefault(); } if (e.key === 'ArrowRight') { move(1); e.preventDefault(); } }}>
        {/* stress zone 0–20% */}
        <rect x={ML} y={Y(20)} width={iw} height={Y(0) - Y(20)} fill="#e74c3c" opacity="0.05" />
        {/* charging shading */}
        {chgBands.map(([a, b], k) => (
          <rect key={k} x={X(a)} y={MT} width={Math.max(X(b) - X(a), 1.5)} height={ih} fill="#27ae60" opacity="0.08" />
        ))}
        {/* grid */}
        {[0, 20, 40, 60, 80, 100].map(p => (
          <g key={p}>
            <line x1={ML} y1={Y(p)} x2={W - MR} y2={Y(p)} stroke="#eceef3" strokeWidth="1" />
            <text x={ML - 6} y={Y(p) + 3} textAnchor="end" className="bat-tick">{p}</text>
          </g>
        ))}
        {[0, 0.25, 0.5, 0.75, 1].map(f => {
          const i = Math.round(f * (series.length - 1));
          return <text key={f} x={X(i)} y={H - 6} textAnchor="middle" className="bat-tick">{fmtTick(series[i].t, hours)}</text>;
        })}
        {/* envelope + line */}
        {envPath && <path d={envPath} fill={DIS} opacity="0.10" />}
        {linePaths.map((d, k) => <path key={k} d={d} fill="none" stroke={DIS} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />)}
        {/* end marker with surface ring */}
        {lastIdx != null && (
          <g>
            <circle cx={X(lastIdx)} cy={Y(series[lastIdx].last)} r="6" fill="#fff" />
            <circle cx={X(lastIdx)} cy={Y(series[lastIdx].last)} r="4" fill={DIS} />
          </g>
        )}
        {/* crosshair */}
        {hb && (
          <g>
            <line x1={X(hover)} y1={MT} x2={X(hover)} y2={MT + ih} stroke="#c3c8d4" strokeWidth="1" />
            <circle cx={X(hover)} cy={Y(hb.last)} r="6" fill="#fff" />
            <circle cx={X(hover)} cy={Y(hb.last)} r="4" fill={DIS} />
          </g>
        )}
      </svg>
      {hb && (
        <div className="bat-tip" style={{ left: `${(X(hover) / W) * 100}%` }}>
          <div className="bat-tip-val">{hb.last}%</div>
          <div className="bat-tip-row">{hb.chgFrac >= 0.5 ? 'charging' : 'on battery'}{hours > 24 && hb.min !== hb.max ? ` · ${hb.min}–${hb.max}%` : ''}</div>
          <div className="bat-tip-row">{fmtFull(hb.t)}</div>
        </div>
      )}
    </div>
  );
}

// ── Rate trend: two validated series, one axis, legend + endpoint labels. ──
function RateTrend({ trend }) {
  const pts = trend.filter(t => t.disRate != null || t.chgRate != null);
  if (pts.length < 2) return null;
  const W = 960, H = 150, ML = 34, MR = 46, MT = 10, MB = 20;
  const iw = W - ML - MR, ih = H - MT - MB;
  const vals = pts.flatMap(p => [Math.abs(p.disRate ?? 0), Math.abs(p.chgRate ?? 0)]);
  const ymax = Math.max(5, Math.ceil(Math.max(...vals) / 5) * 5);
  const X = (i) => ML + (i / (pts.length - 1)) * iw;
  const Y = (v) => MT + (1 - v / ymax) * ih;
  const path = (get) => {
    const seg = pts.map((p, i) => (get(p) != null ? [X(i), Y(Math.abs(get(p)))] : null)).filter(Boolean);
    return seg.length > 1 ? 'M' + seg.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join('L') : '';
  };
  const lastVal = (get) => { for (let i = pts.length - 1; i >= 0; i--) if (get(pts[i]) != null) return { i, v: Math.abs(get(pts[i])) }; return null; };
  const dEnd = lastVal(p => p.disRate), cEnd = lastVal(p => p.chgRate);
  return (
    <div>
      <div className="bat-legend">
        <span className="bat-key"><i style={{ background: DIS }} /> discharge %/h</span>
        <span className="bat-key"><i style={{ background: CHG }} /> charge %/h</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="bat-svg" role="img" aria-label="Median charge and discharge rates over time">
        {[0, ymax / 2, ymax].map(v => (
          <g key={v}>
            <line x1={ML} y1={Y(v)} x2={W - MR} y2={Y(v)} stroke="#eceef3" strokeWidth="1" />
            <text x={ML - 6} y={Y(v) + 3} textAnchor="end" className="bat-tick">{v}</text>
          </g>
        ))}
        {pts.map((p, i) => <text key={i} x={X(i)} y={H - 5} textAnchor="middle" className="bat-tick">{p.label}</text>)}
        <path d={path(p => p.disRate)} fill="none" stroke={DIS} strokeWidth="2" strokeLinejoin="round" />
        <path d={path(p => p.chgRate)} fill="none" stroke={CHG} strokeWidth="2" strokeLinejoin="round" />
        {[[dEnd, DIS], [cEnd, CHG]].map(([e, col], k) => e && (
          <g key={k}>
            <circle cx={X(e.i)} cy={Y(e.v)} r="6" fill="#fff" /><circle cx={X(e.i)} cy={Y(e.v)} r="4" fill={col} />
            <text x={X(e.i) + 8} y={Y(e.v) + 3} className="bat-endlabel">{e.v}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function BatterySection({ thingName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(24);
  const seqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++seqRef.current;
    try {
      setLoading(true);
      const json = await ggosDeviceService.getScreenBattery(thingName, hours);
      if (seq !== seqRef.current) return;
      if (!json.success) throw new Error(json.error || 'failed');
      setData(json.data);
      setError(null);
    } catch (e) { if (seq === seqRef.current) setError(e.message); }
    finally { if (seq === seqRef.current) setLoading(false); }
  }, [thingName, hours]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const st = data?.stats;
  const segs = (data?.segments || []).slice().reverse().slice(0, 40);

  return (
    <div className="bat-section">
      <div className="bat-head">
        <h2>Battery treatment</h2>
        <div className="bat-windows">
          {WINDOWS.map(w => (
            <button key={w.h} className={hours === w.h ? 'is-active' : ''} onClick={() => setHours(w.h)}>{w.label}</button>
          ))}
        </div>
        <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'bat-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && !data && <div className="bat-skel" />}

      {st && (
        <div className={loading ? 'bat-loading-dim' : undefined}>
          <div className="bat-overview">
            <div className="bat-hero">
              <div className="bat-hero-val">{st.currentPct != null ? `${st.currentPct}%` : '—'}</div>
              <div className={`bat-hero-state${st.currentState === 'charging' ? ' is-chg' : ''}`}>{st.currentState || 'no data'}</div>
              <div className="bat-hero-obs">{st.observedH}h observed</div>
            </div>
            <div className="bat-group">
              <div className="bat-group-title">Power mix</div>
              <div className="bat-group-row"><span>plugged</span><b>{st.pctTimeCharging}%</b></div>
              <div className="bat-group-row"><span>float at full</span><b>{st.pctTimeFloat}%</b></div>
              <div className="bat-group-row"><span>on battery</span><b>{100 - st.pctTimeCharging}%</b></div>
            </div>
            <div className="bat-group">
              <div className="bat-group-title">Stress</div>
              <div className="bat-group-row"><span>time ≤20%</span><b className={st.pctTimeLow > 20 ? 'is-bad' : ''}>{st.pctTimeLow}%</b></div>
              <div className="bat-group-row"><span>at 0%</span><b className={st.pctTimeDead > 0 ? 'is-bad' : ''}>{st.pctTimeDead}%</b></div>
              <div className="bat-group-row"><span>plugged, not charging</span><b className={st.pctTimeNotCharging > 0 ? 'is-bad' : ''}>{st.pctTimeNotCharging}%</b></div>
            </div>
            <div className="bat-group">
              <div className="bat-group-title">Cycling</div>
              <div className="bat-group-row"><span>cycles / day</span><b>{st.cyclesPerDay ?? '—'}</b></div>
              <div className="bat-group-row"><span>median depth</span><b>{st.medianDoD != null ? `${st.medianDoD}%` : '—'}</b></div>
              <div className="bat-group-row"><span>charge / discharge</span><b>{st.medianChargeRate != null ? `+${st.medianChargeRate}` : '—'} / {st.medianDischargeRate ?? '—'} %/h</b></div>
            </div>
          </div>

          <div className="bat-card">
            <div className="bat-card-head">
              <div className="bat-card-title">State of charge</div>
              <div className="bat-legend">
                <span className="bat-key"><i className="bat-sw" style={{ background: '#27ae60', opacity: 0.35 }} /> plugged</span>
                <span className="bat-key"><i className="bat-sw" style={{ background: '#e74c3c', opacity: 0.3 }} /> ≤20% zone</span>
                {hours > 24 && <span className="bat-key"><i className="bat-sw" style={{ background: DIS, opacity: 0.25 }} /> min–max</span>}
                <span className="bat-key"><i className="bat-sw bat-sw--gap" /> unobserved</span>
              </div>
            </div>
            <SocChart series={data.series} hours={data.hours} />
          </div>

          {data.trend && data.trend.length > 1 && (
            <div className="bat-card">
              <div className="bat-card-head">
                <div className="bat-card-title">Median charge vs discharge rate</div>
                <div className="bat-card-note">rising discharge at steady duty = aging signature</div>
              </div>
              <RateTrend trend={data.trend} />
            </div>
          )}

          <div className="bat-card">
            <div className="bat-card-head">
              <div className="bat-card-title">Cycle log</div>
              <div className="bat-card-note">derived charge/discharge segments, ≥5min observed · cab = GPS motion during the segment</div>
            </div>
            <table className="bat-table">
              <thead><tr><th>kind</th><th>cab</th><th>start</th><th>from → to</th><th>depth</th><th>rate</th><th>duration</th></tr></thead>
              <tbody>
                {segs.map((s, i) => (
                  <tr key={i}>
                    <td><span className="bat-chip" style={{ background: s.kind === 'charge' ? CHG : DIS }} /> {s.kind}</td>
                    <td>{s.km == null ? '—'
                      : s.gpsPct < 30 ? <span className="bat-cab-nogps">no GPS</span>
                      : s.movingPct >= 50 ? <span className="bat-cab-moving">driving · {s.km} km</span>
                      : s.km >= 0.5 ? <span className="bat-cab-partly">partly · {s.km} km</span>
                      : <span className="bat-cab-parked">parked</span>}</td>
                    <td>{fmtFull(s.t0)}</td>
                    <td>{s.pct0}% → {s.pct1}%</td>
                    <td>{s.depth}%</td>
                    <td>{s.rate > 0 ? `+${s.rate}` : s.rate} %/h</td>
                    <td>{s.durH}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
